/**
 * ClusterModel: a data-driven description of one original instrument cluster.
 * Definitions live in /cluster-models/*.yaml (not in code), so a new car is added
 * by adding a file. This module only defines and validates the shape.
 */
import { z } from 'zod';

const id = z.string().regex(/^[a-z0-9][a-z0-9_-]*$/, 'lowercase letters, digits, "-" and "_" only');

/** Position of an element on the generic SVG preview canvas (0–100 units). */
const layout = z.strictObject({
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
  size: z.number().positive().max(100),
});

export const needleGaugeSchema = z.strictObject({
  id,
  label: z.string(),
  type: z.literal('needle'),
  /** Physical range printed on the original dial, in dial units. */
  min: z.number(),
  max: z.number(),
  /** Unit printed on the original dial (km/h, ×1000 rpm, °C…). */
  unit: z.string(),
  /** Total needle sweep in degrees and the angle of `min` (0 = 12 o'clock, clockwise). */
  sweep_deg: z.number().positive().max(360),
  start_deg: z.number().min(-360).max(360),
  /** Tick spacing for the generic preview, in dial units. */
  major_step: z.number().positive(),
  minor_step: z.number().positive().optional(),
  /** Redline printed on the original dial (dial units), if any. */
  printed_redline: z.number().optional(),
  layout,
});

export const segmentDisplaySchema = z.strictObject({
  id,
  label: z.string(),
  type: z.literal('segment_display'),
  digits: z.number().int().positive().max(32),
  /** 'numeric' = 0-9 space and minus, 'alphanumeric' = A-Z 0-9 space - . %, or an explicit character list. */
  charset: z.union([z.enum(['numeric', 'alphanumeric']), z.string().min(1)]),
  decimal_point: z.boolean().default(false),
  layout,
});

/** Generic lamp symbols the preview can draw (our own simple line icons). */
export const LAMP_SYMBOLS = [
  'generic', 'alert', 'info', 'check', 'fuel', 'temperature', 'battery', 'oil', 'engine', 'beam', 'arrow_left', 'arrow_right',
  'washer_fluid', 'brake', 'tyre_pressure', 'door', 'seatbelt', 'service',
] as const;
export type LampSymbol = (typeof LAMP_SYMBOLS)[number];

export const warningLightSchema = z.strictObject({
  id,
  label: z.string(),
  type: z.literal('warning_light'),
  /** Colour of the lamp as it lights up in the cluster. */
  color: z.enum(['red', 'amber', 'green', 'blue', 'white']),
  /** Generic symbol for the preview (never a replica of a carmaker's symbol artwork). */
  symbol: z.enum(LAMP_SYMBOLS).default('generic'),
  layout,
});

export const gaugeSchema = z.discriminatedUnion('type', [needleGaugeSchema, segmentDisplaySchema, warningLightSchema]);

export const backlightZoneSchema = z.strictObject({
  id,
  label: z.string(),
  /** Gauges whose status colours this zone in "status" colour mode. */
  gauges: z.array(id).default([]),
});

export const clusterModelSchema = z
  .strictObject({
    id,
    /** Descriptive name only, e.g. "VW Golf 7 analog cluster". No logos, crests or brand fonts anywhere. */
    label: z.string(),
    vehicle: z.string(),
    part_numbers: z.array(z.string()).default([]),
    /** false until every value has been checked on a real unit. */
    verified: z.boolean().default(false),
    /** Open items to check on the real unit. Shown in the admin UI. */
    todo: z.array(z.string()).default([]),
    notes: z.string().optional(),
    /** Aspect of the preview canvas (width : height). */
    canvas: z.strictObject({ width: z.number().positive(), height: z.number().positive() }),
    gauges: z.array(gaugeSchema).min(1),
    backlight_zones: z.array(backlightZoneSchema).default([]),
  })
  .superRefine((model, ctx) => {
    const ids = new Set<string>();
    model.gauges.forEach((g, i) => {
      if (ids.has(g.id)) ctx.addIssue({ code: 'custom', path: ['gauges', i, 'id'], message: `duplicate gauge id "${g.id}"` });
      ids.add(g.id);
      if (g.type === 'needle') {
        if (g.max === g.min) ctx.addIssue({ code: 'custom', path: ['gauges', i, 'max'], message: 'max must differ from min' });
        if (g.printed_redline !== undefined && (g.printed_redline < Math.min(g.min, g.max) || g.printed_redline > Math.max(g.min, g.max)))
          ctx.addIssue({ code: 'custom', path: ['gauges', i, 'printed_redline'], message: 'printed_redline outside the dial range' });
      }
    });
    model.backlight_zones.forEach((z, zi) =>
      z.gauges.forEach((gid, gi) => {
        if (!ids.has(gid)) ctx.addIssue({ code: 'custom', path: ['backlight_zones', zi, 'gauges', gi], message: `unknown gauge "${gid}"` });
      }),
    );
  });

export type ClusterModel = z.infer<typeof clusterModelSchema>;
export type Gauge = z.infer<typeof gaugeSchema>;
export type NeedleGauge = z.infer<typeof needleGaugeSchema>;
export type SegmentDisplay = z.infer<typeof segmentDisplaySchema>;
export type WarningLight = z.infer<typeof warningLightSchema>;

/** Validates a parsed YAML/JSON object. Throws with readable paths on error. */
export function parseClusterModel(input: unknown, source = 'cluster model'): ClusterModel {
  const result = clusterModelSchema.safeParse(input);
  if (!result.success) {
    const lines = result.error.issues.map((i) => `  - ${i.path.join('.') || '(root)'}: ${i.message}`);
    throw new Error(`Invalid ${source}:\n${lines.join('\n')}`);
  }
  return result.data;
}

export function findGauge(model: ClusterModel, gaugeId: string): Gauge | undefined {
  return model.gauges.find((g) => g.id === gaugeId);
}
